<template>
  <div class="user-home">
    <!-- 会员状态卡片 -->
    <el-card class="status-card">
      <template #header>
        <div class="card-header">
          <h2>会员状态</h2>
        </div>
      </template>

      <div class="status-content">
        <!-- 未登录 -->
        <div v-if="!isLogin" class="status-section">
          <el-empty description="请先登录">
            <el-button type="primary" @click="goToLogin">立即登录</el-button>
          </el-empty>
        </div>

        <!-- 已登录但未选择会员类型 -->
        <div v-else-if="!hasMemberType" class="status-section">
          <el-result icon="info" title="欢迎加入" sub-title="请选择您的会员类型以继续">
            <template #extra>
              <el-button type="primary" @click="goToTypeSelect">
                选择会员类型
              </el-button>
            </template>
          </el-result>
        </div>

        <!-- 已选择类型但未支付 -->
        <div v-else-if="needsOnlinePayment" class="status-section">
          <el-result icon="warning" title="待支付" :sub-title="`您已选择${memberTypeText}，请完成支付以激活会员权限`">
            <template #extra>
              <div class="pending-actions">
                <el-button v-if="!isOrganization && !isDirector" type="primary" @click="goToPayment">
                  立即支付
                </el-button>
                <el-button plain @click="goToTypeSelect">
                  修改会员类型
                </el-button>
                <el-button plain @click="openMemberInfoDialog">
                  完善会员信息
                </el-button>
              </div>
            </template>
          </el-result>
        </div>

        <!-- 审核中 -->
        <div v-else-if="isPending" class="status-section">
          <el-result icon="info" title="审核中" sub-title="您的会员信息正在审核中，请耐心等待">
            <template #extra>
              <div class="member-details">
                <div class="member-profile">
                  <el-avatar :size="95" :src="memberInfo.photo" shape="square" class="id-photo">
                    {{ memberInfo.name?.charAt(0) }}
                  </el-avatar>
                  <div class="member-profile-info">
                    <h3 class="member-name">{{ memberInfo.name || '-' }}</h3>
                    <p class="member-title" v-if="memberInfo.title">{{ memberInfo.title }}</p>
                    <div class="member-tags">
                      <el-tag type="info">{{ memberTypeText }}</el-tag>
                      <el-tag type="info" v-if="isIndividual">{{ memberLevelText }}</el-tag>
                      <el-tag type="warning">审核中</el-tag>
                    </div>
                  </div>
                </div>
                <el-descriptions :column="2" >
                  <el-descriptions-item label="姓名">{{ memberInfo.name || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="职称">{{ memberInfo.title || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="地址">{{ memberInfo.address || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="支付时间">{{ memberInfo.pay_time || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="注册时间">{{ memberInfo.create_time || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="审核状态">
                    <el-tag type="info" size="small">审核中</el-tag>
                  </el-descriptions-item>
                </el-descriptions>
                <div class="member-intro" v-if="memberInfo.intro">
                  <h3>个人简介</h3>
                  <p>{{ memberInfo.intro }}</p>
                </div>
              </div>
            </template>
          </el-result>
        </div>

        <!-- 已驳回 -->
        <div v-else-if="isRejected" class="status-section">
          <el-result icon="error" title="审核未通过" :sub-title="memberInfo.audit_remark || '您的会员申请未通过审核，请修改后重新提交'">
            <template #extra>
              <div class="member-details">
                <div class="member-profile">
                  <el-avatar :size="95" :src="memberInfo.photo" shape="square" class="id-photo">
                    {{ memberInfo.name?.charAt(0) }}
                  </el-avatar>
                  <div class="member-profile-info">
                    <h3 class="member-name">{{ memberInfo.name || '-' }}</h3>
                    <p class="member-title" v-if="memberInfo.title">{{ memberInfo.title }}</p>
                    <div class="member-tags">
                      <el-tag type="danger">{{ memberTypeText }}</el-tag>
                      <el-tag type="danger" v-if="isIndividual">{{ memberLevelText }}</el-tag>
                      <el-tag type="danger">已驳回</el-tag>
                    </div>
                  </div>
                </div>
                <el-descriptions :column="2" border>
                  <el-descriptions-item label="姓名">{{ memberInfo.name || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="职称">{{ memberInfo.title || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="地址">{{ memberInfo.address || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="驳回原因">
                    <span style="color: #f56c6c">{{ memberInfo.audit_remark || '-' }}</span>
                  </el-descriptions-item>
                </el-descriptions>
                <el-button type="primary" style="margin-top: 20px" @click="openMemberInfoDialog">
                  立即完善信息
                </el-button>
              </div>
            </template>
          </el-result>
        </div>

        <!-- 审核通过 -->
        <div v-else-if="isApproved" class="status-section approved">
          <div class="cert-header">
            <div class="cert-seal">✦</div>
            <h2 class="cert-title">会员证书</h2>
            <p class="cert-subtitle">山东心理咨询行业协会</p>
          </div>

          <div class="member-details">
            <div class="cert-divider"><span>会员信息</span></div>

            <!-- 会员头像与基本信息 -->
            <div class="member-profile">
              <el-avatar :size="95" :src="memberInfo.photo" shape="square" class="id-photo">
                {{ memberInfo.name?.charAt(0) }}
              </el-avatar>
              <div class="member-profile-info">
                <h3 class="member-name">{{ memberInfo.name || '-' }}</h3>
                <p class="member-title-text" v-if="memberInfo.title">{{ memberInfo.title }}</p>
                <div class="member-tags">
                  <el-tag class="cert-tag" effect="dark">{{ memberTypeText }}</el-tag>
                  <el-tag class="cert-tag" effect="dark" v-if="isIndividual">{{ memberLevelText }}</el-tag>
                  <el-tag class="cert-tag cert-tag--verified" effect="dark">已认证</el-tag>
                </div>
              </div>
            </div>

            <div class="cert-divider"><span>详细资料</span></div>

            <!-- 详细信息 -->
            <el-descriptions :column="2" border class="member-desc">
              <el-descriptions-item label="姓名">{{ memberInfo.name || '-' }}</el-descriptions-item>
              <el-descriptions-item label="职称">{{ memberInfo.title || '-' }}</el-descriptions-item>
              <el-descriptions-item label="会员类型">{{ memberTypeText }}</el-descriptions-item>
              <el-descriptions-item label="会员等级" v-if="isIndividual">{{ memberLevelText }}</el-descriptions-item>
              <el-descriptions-item label="支付状态">
                <span :class="memberInfo.pay_status === 1 ? 'status-ok' : 'status-no'">
                  {{ memberInfo.pay_status === 1 ? '已支付' : '未支付' }}
                </span>
              </el-descriptions-item>
              <el-descriptions-item label="支付时间">{{ memberInfo.pay_time || '-' }}</el-descriptions-item>
              <el-descriptions-item label="联系地址">{{ memberInfo.address || '-' }}</el-descriptions-item>
              <el-descriptions-item label="审核状态"><span class="status-ok">审核通过</span></el-descriptions-item>
              <el-descriptions-item label="注册时间">{{ memberInfo.create_time || '-' }}</el-descriptions-item>
              <el-descriptions-item label="更新时间">{{ memberInfo.update_time || '-' }}</el-descriptions-item>
            </el-descriptions>

            <!-- 简介 -->
            <template v-if="memberInfo.intro">
              <div class="cert-divider"><span>个人简介</span></div>
              <div class="member-intro">
                <p>{{ memberInfo.intro }}</p>
              </div>
            </template>

            <!-- 详细介绍 -->
            <template v-if="memberInfo.content">
              <div class="cert-divider"><span>详细介绍</span></div>
              <div class="member-content">
                <div class="content-text">{{ memberInfo.content }}</div>
              </div>
            </template>

            <div class="cert-divider"><span>会员权益</span></div>

            <!-- 会员权益 -->
            <div class="permissions-section">
              <el-row :gutter="16">
                <el-col :span="8" v-for="(perm, index) in permissions" :key="index">
                  <div class="permission-item">
                    <el-icon :size="16" color="#c8a96e"><Check /></el-icon>
                    <span>{{ perm }}</span>
                  </div>
                </el-col>
              </el-row>
            </div>

            <div class="cert-divider"><span>快捷操作</span></div>

            <!-- 快捷操作 -->
            <div class="quick-actions">
              <el-button type="primary" plain @click="goToCourses">浏览课程</el-button>
              <el-button type="primary" plain v-if="isDirector" @click="goToPublishNews">发布新闻</el-button>
              <el-button plain @click="goToMemberInfo">修改信息</el-button>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 完善信息弹窗 -->
    <MemberInfoDialog 
      v-model="dialogVisible" 
      :initial-data="dialogInitialData"
      @submit="handleDialogSubmit" 
    />
  </div>
</template>

<script>
import { Check } from '@element-plus/icons-vue'
import MemberInfoDialog from '@/components/MemberInfoDialog.vue'
import {
  MEMBER_LEVEL,
  MEMBER_TYPE_NAMES,
  MEMBER_LEVEL_NAMES
} from '@/utils/constants'
import categoryApi from '@/api/index'
import memberApi from "@/api/member";

export default {
  components: { Check, MemberInfoDialog },
  data() {
    return {
      dialogVisible: false,
      dialogInitialData: {}
    }
  },
  computed: {
    isLogin() { return this.$store.getters['auth/isLogin'] },
    memberInfo() { return this.$store.getters['auth/memberInfo'] },
    memberType() { return this.$store.getters['auth/memberType'] },
    memberLevel() { return this.$store.getters['auth/memberLevel'] },
    hasPaid() { return this.$store.getters['auth/hasPaid'] },
    isIndividual() { return this.$store.getters['auth/isIndividual'] },
    isOrganization() { return this.$store.getters['auth/isOrganization'] },
    isDirector() { return this.$store.getters['auth/isDirector'] },
    isPending() { return this.$store.getters['auth/isPending'] },
    isApproved() { return this.$store.getters['auth/isApproved'] },
    isRejected() { return this.$store.getters['auth/isRejected'] },
    hasMemberType() { return this.memberType !== null && this.memberType !== undefined },
    needsOnlinePayment() { return this.isIndividual && !this.hasPaid },
    memberTypeText() { return MEMBER_TYPE_NAMES[this.memberType] || '-' },
    memberLevelText() { return MEMBER_LEVEL_NAMES[this.memberLevel] || '-' },
    permissions() {
      const perms = []
      if (this.isIndividual) {
        perms.push('浏览课程信息', '购买课程')
        if (this.memberLevel === MEMBER_LEVEL.REGISTERED) {
          perms.push('个人信息展示', '优先参与活动')
        }
      } else if (this.isOrganization) {
        perms.push('机构信息展示', '社会服务搜索', '查看完整会员信息')
      } else if (this.isDirector) {
        perms.push('浏览课程信息', '购买课程', '发布新闻', '管理新闻', '参与协会决策')
      }
      return perms
    }
  },
  async mounted() {
    if (this.isLogin) {
      await this.fetchMemberInfo()
    }
  },
  methods: {
    /**
     * 获取会员信息
     * 处理 code: 4000 的情况，引导用户选择会员类型并完善信息
     */
    async fetchMemberInfo() {
      try {
        const res = await categoryApi.info()
        
        if (res && res.code === 2000) {
          // 成功获取会员信息
          this.$store.dispatch('auth/refreshMemberInfo', res.data)
        } else if (res && res.code === 4000) {
          this.$store.commit('auth/clearMemberInfo')
          localStorage.removeItem('this_user_member')
          localStorage.removeItem('pending_member_type')
          localStorage.removeItem('pending_member_level')
          localStorage.removeItem('member_info_completed')
        }
      } catch (e) {
        console.error('获取会员信息失败', e)
        this.$message.error('获取会员信息失败，请稍后重试')
      }
    },
    
    goToLogin() { this.$router.push('/login') },
    goToTypeSelect() { this.$router.push('/user/member-type-select') },
    goToPayment() {
      this.$router.push({
        path: '/user/member-payment',
        query: { type: this.memberType, level: this.memberLevel }
      })
    },
    goToMemberInfo() { this.openMemberInfoDialog() },
    goToCourses() { this.$router.push('/course/list') },
    goToPublishNews() { this.$router.push('/news/publish') },

    // 打开完善信息弹窗
    openMemberInfoDialog() {
      // 加载现有会员信息到弹窗表单
      if (this.memberInfo && this.memberInfo.id) {
        this.dialogInitialData = {
          realName: this.memberInfo.name || '',
          gender: this.memberInfo.gender || '',
          phone: this.memberInfo.phone || '',
          email: this.memberInfo.email || '',
          birthday: this.memberInfo.birthday || '',
          company: this.memberInfo.company || '',
          title: this.memberInfo.title || '',
          education: this.memberInfo.education || '本科',
          city: this.memberInfo.city || '',
          degreeNo: this.memberInfo.degree_no || '',
          graduateSchool: this.memberInfo.graduate_school || '',
          major: this.memberInfo.major || '',
          startDate: this.memberInfo.start_date || '',
          experiences: this.memberInfo.experiences ? 
            (typeof this.memberInfo.experiences === 'string' ? 
              JSON.parse(this.memberInfo.experiences) : 
              this.memberInfo.experiences) : 
            [
              { time: [], location: '', supervisorName: '', supervisorHours: '' },
              { time: [], location: '', supervisorName: '', supervisorHours: '' }
            ],
          isMember: this.memberInfo.is_member || '否',
          hasViolation: this.memberInfo.has_violation || '否',
          serviceCount: this.memberInfo.service_count || '',
          caseCount: this.memberInfo.case_count || '',
          remark: this.memberInfo.remark || '',
          photoUrl: this.memberInfo.photo || '',
          certificateUrl: this.memberInfo.certificate || '',
          degreeUrl: this.memberInfo.degree_certificate || ''
        }
      }
      this.dialogVisible = true
    },

    // 处理弹窗提交
    async handleDialogSubmit(submitData) {
      try {
        const res = await memberApi.updateMemberInfo(submitData);

        if (res && res.code === 2000) {
          this.$message.success(res.msg || '提交成功！')
          this.dialogVisible = false

          // 刷新会员信息
          if (res.data) {
            await this.$store.dispatch('auth/refreshMemberInfo', res.data)
          }

          await this.fetchMemberInfo()
        }
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('提交失败，请稍后重试')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.user-home {
  min-height: 100vh;
  padding: 40px 20px;
  background-color: #f5f7fa;

  .status-card {
    max-width: 1400px;
    margin: 0 auto;

    .card-header {
      text-align: center;

      h2 {
        margin: 0;
        font-size: 24px;
        color: #303133;
      }
    }

    .status-content {
      min-height: 400px;

      .status-section {
        padding: 40px 20px;

        .pending-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
          margin-top: 20px;

          .el-button {
            min-width: 140px;
            border-radius: 4px;
            letter-spacing: 1px;
            font-weight: 500;
          }
        }

        &.approved {
          padding: 32px 40px;
          background-color: #fff;

          .cert-header {
            text-align: center;
            padding: 24px 0 20px;
            border-bottom: 2px solid #1a3a6b;
            margin-bottom: 28px;

            .cert-seal {
              font-size: 28px;
              color: #c8a96e;
              line-height: 1;
              margin-bottom: 8px;
            }

            .cert-title {
              margin: 0 0 6px;
              font-size: 26px;
              font-weight: 700;
              color: #1a3a6b;
              letter-spacing: 6px;
            }

            .cert-subtitle {
              margin: 0;
              font-size: 14px;
              color: #909399;
              letter-spacing: 3px;
            }
          }

          .cert-divider {
            display: flex;
            align-items: center;
            margin: 20px 0 16px;
            color: #1a3a6b;
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 2px;

            &::before, &::after {
              content: '';
              flex: 1;
              height: 1px;
              background-color: #dce3ef;
            }

            span {
              padding: 0 14px;
            }
          }

          .status-ok { color: #1a7a4a; font-weight: 600; }
          .status-no { color: #c0392b; font-weight: 600; }
        }

        .member-details {
          width: 100%;
          margin-top: 20px;

          .member-profile {
            display: flex;
            align-items: center;
            gap: 24px;
            padding: 24px;
            background: linear-gradient(135deg, #f5f8ff 0%, #faf8f2 100%);
            border: 1px solid #e4e7ed;
            border-radius: 4px;
            margin-bottom: 24px;

            .id-photo {
              width: 95px !important;
              height: 133px !important;
              flex-shrink: 0;
            }

            .member-profile-info {
              .member-name {
                margin: 0 0 6px 0;
                font-size: 22px;
                color: #1a3a6b;
                font-weight: 700;
              }

              .member-title-text {
                margin: 0 0 12px 0;
                font-size: 14px;
                color: #606266;
                letter-spacing: 1px;
              }

              .member-tags {
                display: flex;
                gap: 8px;

                .cert-tag {
                  background-color: #1a3a6b;
                  border-color: #1a3a6b;
                  border-radius: 2px;
                  letter-spacing: 1px;
                }

                .cert-tag--verified {
                  background-color: #c8a96e;
                  border-color: #c8a96e;
                }
              }
            }
          }

          .member-desc {
            margin-bottom: 24px;

            :deep(.el-descriptions__label) {
              color: #1a3a6b;
              font-weight: 600;
              background-color: #f5f7fa;
              width: 100px;
            }

            :deep(.el-descriptions__content) {
              color: #303133;
            }
          }

          .member-intro,
          .member-content {
            padding: 16px 20px;
            background-color: #fafafa;
            border-left: 3px solid #c8a96e;
            margin-bottom: 8px;

            p, .content-text {
              color: #606266;
              font-size: 14px;
              line-height: 2;
              margin: 0;
            }
          }

          .permissions-section {
            padding: 4px 0 16px;

            .permission-item {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 10px 14px;
              background-color: #f9f8f4;
              border: 1px solid #e8e0cc;
              border-radius: 2px;
              margin-bottom: 10px;

              span {
                color: #303133;
                font-size: 13px;
              }
            }
          }

          .quick-actions {
            padding: 4px 0 8px;
            display: flex;
            gap: 12px;

            .el-button {
              border-radius: 2px;
              letter-spacing: 1px;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .user-home {
    padding: 20px 10px;

    .status-card {
      .status-section {
        padding: 20px 10px;

        .pending-actions {
          flex-direction: column;
          gap: 10px;

          .el-button {
            width: 100%;
          }
        }

        &.approved {
          .member-details {
            .permissions-section {
              .el-col {
                margin-bottom: 10px;
              }
            }

            .quick-actions {
              .el-col {
                margin-bottom: 10px;
              }
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
/* 完善信息弹窗样式 - 与确认弹窗保持一致的正式庄重风格 */
.member-info-dialog {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(26, 58, 107, 0.2);

  .el-dialog__header {
    padding: 0;
    margin-right: 0;
  }

  .el-dialog__body {
    padding: 32px 40px;
    max-height: 65vh;
    overflow-y: auto;
    background: linear-gradient(180deg, #fafbfc 0%, #ffffff 100%);
  }

  .el-dialog__footer {
    padding: 0;
    border-top: 1px solid #e8eef5;
  }

  .el-dialog__headerbtn {
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    font-size: 20px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    .el-dialog__close {
      color: #5a6c7d;
      transition: all 0.3s ease;

      &:hover {
        color: #1a3a6b;
      }
    }
  }

  .info-dialog-header {
    text-align: center;
    padding: 0;
    background: linear-gradient(180deg, #f8f9fc 0%, #ffffff 100%);
    border-bottom: 2px solid #1a3a6b;
    position: relative;

    .header-logo {
      padding: 28px 0 16px;

      .logo-icon {
        width: 72px;
        height: 72px;
        margin: 0 auto;
        background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        box-shadow: 0 4px 16px rgba(26, 58, 107, 0.25);
        border: 3px solid #fff;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          inset: -6px;
          border-radius: 50%;
          border: 1px solid rgba(26, 58, 107, 0.15);
        }
      }
    }

    .info-dialog-title {
      margin: 0 0 8px;
      font-size: 24px;
      font-weight: 700;
      color: #1a3a6b;
      letter-spacing: 2px;
      font-family: 'Microsoft YaHei', 'SimHei', sans-serif;
    }

    .info-dialog-org {
      margin: 0 0 20px;
      font-size: 14px;
      color: #5a6c7d;
      letter-spacing: 3px;
      font-weight: 500;
    }

    .header-divider {
      height: 3px;
      background: linear-gradient(90deg, transparent 0%, #c8a96e 50%, transparent 100%);
      margin: 0 auto;
      width: 60%;
    }
  }

  .info-dialog-footer {
    display: flex;
    justify-content: center;
    gap: 16px;
    padding: 20px 40px 32px;
    background-color: #fafbfc;

    .footer-btn {
      min-width: 140px;
      height: 46px;
      font-size: 15px;
      font-weight: 600;
      letter-spacing: 1px;
      border-radius: 4px;
      transition: all 0.3s ease;

      &.footer-btn--cancel {
        border: 1px solid #d1d5db;
        color: #6b7280;
        background: #ffffff;

        &:hover {
          border-color: #9ca3af;
          color: #4b5563;
          background: #f9fafb;
        }
      }

      &.footer-btn--primary {
        background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
        border: none;
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(26, 58, 107, 0.25);

        &:hover {
          background: linear-gradient(135deg, #2c5282 0%, #1a3a6b 100%);
          box-shadow: 0 6px 16px rgba(26, 58, 107, 0.35);
          transform: translateY(-1px);
        }

        &:active {
          transform: translateY(0);
        }
      }
    }
  }

  @media (max-width: 768px) {
    width: 92% !important;
    margin: 0 auto;

    .el-dialog__body {
      padding: 24px 24px;
    }

    .info-dialog-header {
      .header-logo {
        padding: 24px 0 12px;

        .logo-icon {
          width: 64px;
          height: 64px;
        }
      }

      .info-dialog-title {
        font-size: 20px;
      }

      .info-dialog-org {
        font-size: 13px;
      }
    }

    .info-dialog-footer {
      flex-direction: column;
      gap: 12px;
      padding: 16px 24px 24px;

      .footer-btn {
        width: 100%;
      }
    }
  }
}

/* 表单样式优化 */
.member-info-form {
  .el-form-item {
    margin-bottom: 24px;

    .el-form-item__label {
      color: #1a3a6b;
      font-weight: 600;
      font-size: 14px;
      line-height: 40px;
    }

    .el-input__wrapper {
      box-shadow: 0 0 0 1px #d0d7de inset;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 0 0 1px #1a3a6b inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px #1a3a6b inset, 0 0 0 3px rgba(26, 58, 107, 0.1);
      }
    }

    .el-textarea__inner {
      border-color: #d0d7de;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:hover {
        border-color: #1a3a6b;
      }

      &:focus {
        border-color: #1a3a6b;
        box-shadow: 0 0 0 3px rgba(26, 58, 107, 0.1);
      }
    }

    .el-radio-group {
      .el-radio {
        margin-right: 20px;

        .el-radio__label {
          color: #303133;
          font-size: 14px;
        }

        &.is-checked {
          .el-radio__inner {
            background-color: #1a3a6b;
            border-color: #1a3a6b;
          }

          .el-radio__label {
            color: #1a3a6b;
            font-weight: 500;
          }
        }
      }
    }

    .el-checkbox {
      .el-checkbox__label {
        color: #303133;
        font-size: 14px;
      }

      &.is-checked {
        .el-checkbox__inner {
          background-color: #1a3a6b;
          border-color: #1a3a6b;
        }

        .el-checkbox__label {
          color: #1a3a6b;
          font-weight: 500;
        }
      }
    }
  }

  .el-divider {
    margin: 32px 0 24px;
    border-top: 2px solid #e8eef5;

    .el-divider__text {
      background-color: #fff;
      color: #1a3a6b;
      font-weight: 600;
      font-size: 15px;
      letter-spacing: 1px;
      padding: 0 20px;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 16px;
        background: linear-gradient(180deg, #1a3a6b 0%, #c8a96e 100%);
        border-radius: 2px;
      }
    }
  }
}

/* 上传区域样式 */
.upload-section {
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fc 0%, #fafbfc 100%);
  border: 2px solid #e8eef5;
  border-radius: 12px;
  margin-top: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 20px;
    right: 20px;
    height: 2px;
    background: linear-gradient(90deg, transparent 0%, #c8a96e 50%, transparent 100%);
  }

  .el-row {
    margin: 0 -10px;
  }

  .el-col {
    padding: 0 10px;
  }

  .el-form-item {
    margin-bottom: 0;

    .el-form-item__label {
      display: none;
    }

    .el-form-item__content {
      margin-left: 0 !important;
    }
  }
}

.upload-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8eef5;
  transition: all 0.3s ease;

  &:hover {
    border-color: #c8a96e;
    box-shadow: 0 4px 12px rgba(200, 169, 110, 0.15);
  }
}

.upload-box {
  width: 140px;
  height: 140px;
  border: 2px dashed #d0d7de;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(26, 58, 107, 0.05) 0%, rgba(200, 169, 110, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(135deg, #1a3a6b 0%, #c8a96e 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: transparent;
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(26, 58, 107, 0.2);

    &::before {
      opacity: 1;
    }

    &::after {
      opacity: 1;
    }
  }

  .upload-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
  }

  .upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #5a6c7d;
    z-index: 1;

    .el-icon {
      color: #1a3a6b;
      font-size: 32px;
      margin-bottom: 8px;
      transition: all 0.3s ease;
    }

    span {
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.5px;
    }
  }

  &:hover .upload-placeholder .el-icon {
    transform: scale(1.1);
    color: #c8a96e;
  }
}

.upload-box--id-photo {
  width: 105px;
  height: 147px;
}

.upload-tip {
  margin-top: 12px;
  color: #5a6c7d;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
  font-weight: 500;
  padding: 6px 12px;
  background: linear-gradient(135deg, #f0f4f8 0%, #e8eef5 100%);
  border-radius: 6px;
  border: 1px solid #e0e6ed;
}

.experience-item {
  background: #fff;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: 2px solid #e8eef5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:hover {
    border-color: #c8a96e;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .exp-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid #f0f2f5;

    .exp-title {
      font-size: 15px;
      color: #1a3a6b;
      font-weight: 600;
      letter-spacing: 1px;
      display: flex;
      align-items: center;

      &::before {
        content: '';
        display: inline-block;
        width: 4px;
        height: 16px;
        background: linear-gradient(180deg, #1a3a6b 0%, #c8a96e 100%);
        border-radius: 2px;
        margin-right: 8px;
      }
    }

    .el-button {
      &:hover {
        color: #c8342e;
      }
    }
  }
}

.upload-box {
  width: 120px;
  height: 120px;
  border: 2px dashed #d0d7de;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(26, 58, 107, 0.05) 0%, rgba(200, 169, 110, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: #1a3a6b;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(26, 58, 107, 0.15);

    &::before {
      opacity: 1;
    }
  }

  .upload-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #5a6c7d;
    z-index: 1;

    .el-icon {
      color: #1a3a6b;
      font-size: 28px;
      margin-bottom: 4px;
    }

    span {
      font-size: 12px;
      font-weight: 500;
    }
  }
}

.upload-box--id-photo {
  width: 90px;
  height: 126px;
}

.upload-tip {
  margin-top: 8px;
  color: #5a6c7d;
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
  font-weight: 500;
}

/* 添加经历按钮样式 */
.add-experience-btn-wrapper {
  text-align: center;
  margin: 24px 0;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fc 0%, #fafbfc 100%);
  border-radius: 8px;
  border: 2px dashed #e8eef5;
  transition: all 0.3s ease;

  &:hover {
    border-color: #1a3a6b;
    background: linear-gradient(135deg, #f5f8ff 0%, #fafbfc 100%);
  }

  .el-button {
    border-color: #1a3a6b;
    color: #1a3a6b;
    border-radius: 6px;
    font-weight: 600;
    letter-spacing: 0.5px;
    padding: 12px 32px;
    font-size: 14px;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
      color: #fff;
      border-color: #1a3a6b;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(26, 58, 107, 0.25);
    }
  }
}
</style>
